import { useState } from "react";
import api from "../../core/api/api";

/* Admin login: Password / OTP / Forgot-password (OTP -> new password) */
export default function AdminLogin({ onLoggedIn }) {
  const [mode, setMode] = useState("password"); // password | otp | forgot
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const clearMsg = () => { setError(""); setInfo(""); };

  const handleErr = (e) => {
    const status = e?.response?.status;
    const msg = e?.response?.data?.message;
    if (status === 429) setError("⏳ Too many attempts! Wait 5 min and try again.");
    else setError(msg || "Something went wrong. Please try again.");
  };

  const finishLogin = async () => {
    const me = await api.get("/admin/me");
    if (me.data?.role === "admin") onLoggedIn();
    else setError("Not an admin account");
  };

  const passwordLogin = async () => {
    clearMsg();
    if (!mobile || !password) { setError("Enter mobile and password"); return; }
    setBusy(true);
    try { await api.post("/admin/login", { mobile, password }); await finishLogin(); }
    catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  const sendOtp = async () => {
    clearMsg();
    if (!/^\d{10}$/.test(mobile)) { setError("Enter a valid 10-digit mobile"); return; }
    setBusy(true);
    try {
      await api.post("/admin/send-otp", { mobile });
      setOtpSent(true);
      setInfo("OTP sent (if this number is registered as admin).");
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  const otpLogin = async () => {
    clearMsg();
    if (!otp) { setError("Enter the OTP"); return; }
    setBusy(true);
    try { await api.post("/admin/verify-otp", { mobile, otp }); await finishLogin(); }
    catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  const resetPassword = async () => {
    clearMsg();
    if (!otp || !newPassword) { setError("Enter OTP and new password"); return; }
    setBusy(true);
    try {
      await api.post("/admin/reset-password", { mobile, otp, newPassword });
      setMode("password"); setOtp(""); setNewPassword(""); setOtpSent(false);
      setInfo("✔ Password updated! Login with your new password.");
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  const switchMode = (m) => { setMode(m); setOtp(""); setNewPassword(""); setOtpSent(false); clearMsg(); };

  const S = {
    label: { fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 },
    input: { width: "100%", boxSizing: "border-box", padding: "11px 14px", borderRadius: 12, border: "1.5px solid #e2e8f0", fontSize: 14, outline: "none", color: "#0f172a", marginBottom: 14 },
    btn: { width: "100%", padding: "13px 0", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer", boxShadow: "0 4px 16px rgba(99,102,241,0.4)" },
    tab: (active) => ({ flex: 1, padding: "9px 0", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, background: active ? "#fff" : "transparent", color: active ? "#0f172a" : "#94a3b8", borderRadius: 9, boxShadow: active ? "0 1px 4px rgba(0,0,0,0.12)" : "none" }),
    link: { background: "none", border: "none", color: "#6366f1", fontSize: 13, fontWeight: 700, cursor: "pointer", padding: 0 },
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#0f172a 0%,#1e1b4b 50%,#0f172a 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 400, background: "#fff", borderRadius: 24, padding: "36px 36px", boxShadow: "0 24px 80px rgba(0,0,0,0.4)" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 900, fontSize: 26, margin: "0 auto 12px" }}>V</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#0f172a" }}>VARAii Admin</div>
          <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
            {mode === "forgot" ? "Reset your password" : "Sign in to manage your store"}
          </div>
        </div>

        {/* Tabs (password / otp) — hidden in forgot mode */}
        {mode !== "forgot" && (
          <div style={{ display: "flex", gap: 4, background: "#f1f5f9", borderRadius: 11, padding: 3, marginBottom: 20 }}>
            <button style={S.tab(mode === "password")} onClick={() => switchMode("password")}>Password</button>
            <button style={S.tab(mode === "otp")} onClick={() => switchMode("otp")}>OTP</button>
          </div>
        )}

        {/* Mobile (always) */}
        <label style={S.label}>MOBILE NUMBER</label>
        <input style={S.input} placeholder="10-digit mobile" value={mobile} inputMode="numeric" maxLength={10}
          onChange={e => setMobile(e.target.value.replace(/\D/g, ""))} />

        {/* PASSWORD MODE */}
        {mode === "password" && (
          <>
            <label style={S.label}>PASSWORD</label>
            <input style={S.input} type="password" placeholder="••••••••" value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && passwordLogin()} />
          </>
        )}

        {/* OTP MODE */}
        {mode === "otp" && otpSent && (
          <>
            <label style={S.label}>OTP</label>
            <input style={S.input} placeholder="6-digit OTP" value={otp} inputMode="numeric" maxLength={6}
              onChange={e => setOtp(e.target.value.replace(/\D/g, ""))} />
          </>
        )}

        {/* FORGOT MODE */}
        {mode === "forgot" && otpSent && (
          <>
            <label style={S.label}>OTP</label>
            <input style={S.input} placeholder="6-digit OTP" value={otp} inputMode="numeric" maxLength={6}
              onChange={e => setOtp(e.target.value.replace(/\D/g, ""))} />
            <label style={S.label}>NEW PASSWORD</label>
            <input style={S.input} type="password" placeholder="Min 8 chars, letter + number" value={newPassword}
              onChange={e => setNewPassword(e.target.value)} />
          </>
        )}

        {error && <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#dc2626", fontWeight: 600 }}>❌ {error}</div>}
        {info && <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#15803d", fontWeight: 600 }}>{info}</div>}

        {/* Action button */}
        {mode === "password" && (
          <button style={S.btn} disabled={busy} onClick={passwordLogin}>{busy ? "Signing in…" : "Sign In →"}</button>
        )}
        {mode === "otp" && (
          <button style={S.btn} disabled={busy} onClick={otpSent ? otpLogin : sendOtp}>
            {busy ? "Please wait…" : otpSent ? "Verify & Sign In →" : "Send OTP"}
          </button>
        )}
        {mode === "forgot" && (
          <button style={S.btn} disabled={busy} onClick={otpSent ? resetPassword : sendOtp}>
            {busy ? "Please wait…" : otpSent ? "Reset Password" : "Send OTP"}
          </button>
        )}

        {/* Footer links */}
        <div style={{ textAlign: "center", marginTop: 16 }}>
          {mode === "password" && <button style={S.link} onClick={() => switchMode("forgot")}>Forgot password?</button>}
          {mode === "forgot" && <button style={S.link} onClick={() => switchMode("password")}>← Back to login</button>}
          {mode === "otp" && otpSent && <button style={S.link} onClick={() => { setOtpSent(false); setOtp(""); clearMsg(); }}>← Change number</button>}
        </div>
      </div>
    </div>
  );
}
